import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Gavel, Check, Signature, ShieldAlert } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusChip } from "@/components/StatusChip";
import { OUTCOME_META, formatDate, formatDateTime, specificityCheck } from "@/lib/format";

const OUTCOMES = [
  { key: "met", label: "Met", desc: "Standard restored / recognized.", tone: "green" },
  { key: "extended", label: "Extended", desc: "New date + rationale — only with documented progress.", tone: "amber" },
  { key: "not_met", label: "Not Met", desc: "Reassignment or respectful separation.", tone: "red" },
];

function SignoffRow({ label, signoff, onSign, canSign, testId }) {
  return (
    <div className="flex items-center justify-between rounded-xl bg-patria-sage/40 px-4 py-3" data-testid={testId}>
      <div className="flex items-center gap-2">
        <Signature className="h-4 w-4 text-patria-muted" />
        <span className="text-sm text-patria-ink">{label}</span>
      </div>
      {signoff ? (
        <span className="inline-flex items-center gap-1.5 text-sm text-patria-green"><Check className="h-4 w-4" /> {signoff.name} · {formatDateTime(signoff.at)}</span>
      ) : canSign ? (
        <Button size="sm" onClick={onSign} className="rounded-full bg-patria-green hover:bg-patria-greenDeep text-patria-surface" data-testid={`${testId}-button`}>Sign off</Button>
      ) : (
        <span className="text-xs text-patria-muted">Pending</span>
      )}
    </div>
  );
}

export function DecisionSection({ c, me }) {
  const qc = useQueryClient();
  const plan = c.plan;
  const decision = c.decision;
  const [outcome, setOutcome] = useState("met");
  const [rationale, setRationale] = useState("");
  const [action, setAction] = useState("reassignment");
  const [extensionDate, setExtensionDate] = useState("");
  const [goalResults, setGoalResults] = useState(() => (plan?.goals || []).map((_, i) => ({ goal_index: i, result: "" })));

  const canRecord = (me.role === "manager" || me.role === "leadership") && plan?.status === "active" && ["plan_active", "in_checkins"].includes(c.status);
  const showSection = decision || canRecord;

  const record = useMutation({
    mutationFn: async () => api.post(`/cases/${c.id}/decision`, {
      outcome, rationale, action: outcome === "not_met" ? action : null,
      extension_date: outcome === "extended" ? extensionDate : null, goal_results: goalResults,
    }),
    onSuccess: () => { toast.success("Decision recorded — routed for sign-off"); qc.invalidateQueries({ queryKey: ["case", c.id] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  const signoff = useMutation({
    mutationFn: async (role) => api.post(`/cases/${c.id}/decision/signoff`, { role }),
    onSuccess: (res) => { toast.success(res.data.finalized ? "Decision finalized" : "Sign-off recorded"); qc.invalidateQueries({ queryKey: ["case", c.id] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  if (!showSection) return null;

  const rationaleOk = specificityCheck(rationale, { minLen: 30 }).ok;
  const valid = rationaleOk && (outcome !== "extended" || extensionDate);

  return (
    <Card className="bg-patria-surface border-patria-muted/15 p-6" data-testid="decision-section">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-patria-terracotta/10 flex items-center justify-center"><Gavel className="h-4 w-4 text-patria-terracotta" /></div>
          <div>
            <h3 className="font-heading font-semibold text-patria-ink">Stage 4 · Decision</h3>
            <p className="text-xs text-patria-muted">Requires People Ops sign-off — plus Leadership for separation.</p>
          </div>
        </div>
        {decision && <StatusChip label={OUTCOME_META[decision.outcome]?.label} tone={OUTCOME_META[decision.outcome]?.tone} testId="decision-status" />}
      </div>

      {decision ? (
        <div className="space-y-4" data-testid="decision-view">
          {decision.action === "separation" && (
            <div className="flex items-center gap-2 rounded-xl bg-patria-terracottaDeep/10 px-4 py-2.5 text-sm text-patria-terracottaDeep"><ShieldAlert className="h-4 w-4" /> Outcome: respectful separation — Executive Leadership sign-off required.</div>
          )}
          {decision.action === "reassignment" && <p className="text-sm text-patria-muted">Action: reassignment.</p>}
          {decision.outcome === "extended" && <p className="text-sm text-patria-muted">New review date: {formatDate(decision.extension_date)}</p>}
          <div>
            <p className="text-xs font-semibold text-patria-muted uppercase tracking-wide">Rationale</p>
            <p className="text-sm text-patria-ink mt-1 whitespace-pre-wrap">{decision.rationale}</p>
          </div>
          {decision.goal_results?.some((g) => g.result) && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-patria-muted uppercase tracking-wide">Evidence against goals</p>
              {decision.goal_results.map((g, i) => g.result && <p key={i} className="text-sm text-patria-ink"><span className="text-patria-muted">Goal {g.goal_index + 1}:</span> {g.result}</p>)}
            </div>
          )}
          <p className="text-xs text-patria-muted">Recorded by {decision.recorded_by?.name} · {formatDateTime(decision.recorded_at)}</p>
          <div className="space-y-2 pt-2">
            <SignoffRow label="People Operations sign-off" signoff={decision.people_ops_signoff} canSign={me.role === "people_ops" || me.role === "leadership"} onSign={() => signoff.mutate("people_ops")} testId="signoff-peopleops" />
            {decision.action === "separation" && (
              <SignoffRow label="Executive Leadership sign-off" signoff={decision.leadership_signoff} canSign={me.role === "leadership"} onSign={() => signoff.mutate("leadership")} testId="signoff-leadership" />
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="decision-form">
          <div className="grid gap-2 sm:grid-cols-3">
            {OUTCOMES.map((o) => (
              <button key={o.key} type="button" onClick={() => setOutcome(o.key)} data-testid={`decision-outcome-${o.key}`}
                className={`rounded-xl border p-3 text-left transition-colors ${outcome === o.key ? "border-patria-green bg-patria-green/8" : "border-patria-muted/25 hover:border-patria-green/40"}`}>
                <StatusChip label={o.label} tone={o.tone} />
                <p className="text-xs text-patria-muted mt-2">{o.desc}</p>
              </button>
            ))}
          </div>

          {outcome === "extended" && (
            <div className="space-y-1.5">
              <Label className="text-patria-ink">New final-review date</Label>
              <Input type="date" value={extensionDate} onChange={(e) => setExtensionDate(e.target.value)} data-testid="decision-extension-date" className="bg-patria-surface border-patria-muted/30 w-auto" />
              <p className="text-xs text-patria-muted">Extensions are only allowed with documented progress (a logged check-in).</p>
            </div>
          )}

          {outcome === "not_met" && (
            <div className="space-y-2">
              <Label className="text-patria-ink">Action</Label>
              <div className="flex gap-2">
                {["reassignment", "separation"].map((a) => (
                  <button key={a} type="button" onClick={() => setAction(a)} data-testid={`decision-action-${a}`}
                    className={`rounded-full border px-4 py-2 text-sm capitalize transition-colors ${action === a ? "border-patria-terracottaDeep bg-patria-terracottaDeep/8 text-patria-terracottaDeep" : "border-patria-muted/25 text-patria-ink"}`}>{a}</button>
                ))}
              </div>
              {action === "separation" && <p className="text-xs text-patria-terracottaDeep">Separation requires Executive Leadership sign-off in addition to People Operations.</p>}
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-patria-ink font-medium">Rationale with evidence <span className="text-patria-terracotta">*</span></Label>
            <Textarea value={rationale} onChange={(e) => setRationale(e.target.value)} rows={3} placeholder="Summarize the outcome with specific evidence tied to the goals & measures." data-testid="decision-rationale" className="bg-patria-surface border-patria-muted/30" />
            {rationale && !rationaleOk && <p className="text-xs text-patria-terracottaDeep">Add more specific evidence (min 30 characters).</p>}
          </div>

          <div className="space-y-2">
            <Label className="text-patria-ink">Evidence against each goal</Label>
            {plan?.goals.map((g, i) => (
              <div key={i} className="space-y-1">
                <p className="text-xs text-patria-muted">Goal {i + 1}: {g.expectation}</p>
                <Input value={goalResults[i]?.result || ""} onChange={(e) => setGoalResults((arr) => arr.map((x, j) => (j === i ? { ...x, result: e.target.value } : x)))} placeholder="Result against the measure…" data-testid={`decision-goal-result-${i}`} className="bg-patria-surface border-patria-muted/30" />
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <Button onClick={() => record.mutate()} disabled={!valid || record.isPending} data-testid="decision-submit" className="rounded-full bg-patria-terracotta hover:bg-patria-terracottaDeep text-patria-surface">Record decision</Button>
          </div>
        </div>
      )}
    </Card>
  );
}
